import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTypedParams } from 'react-router-typesafe-routes';
import { z } from 'zod';
import FormInput from '@/components/common/form-input';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useUploadFile from '@/hooks/files/use-upload-file';
import useGroupInfo, { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import useUpdateGroupDetails from '@/hooks/groups/use-update-group-details';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import { uploadableScheme } from '@/models/uploadable';
import ROUTES from '@/routes';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { generateSessionKey } from '@/utils/session';
import { convertUrlToUploadable } from '@/utils/uploadable';

export default function UpdateGroupInfoRoute() {
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS.ADMIN.INFO);
  const queryClient = useQueryClient();
  const { data: groupInfo, isLoading: isLoadingGroup, canManage } = useGroupInfo(groupId);
  const { t } = useTranslation();
  const { mutateAsync: updateGroup, isPending: isUpdating } = useUpdateGroupDetails(groupId);
  const { upload, isLoading: isUploading } = useUploadFile();
  useAdjustLayout();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<UpdateGroupInfoFormData>({
    disabled: !canManage,
    resolver: zodResolver(updateGroupInfoFormScheme),
    defaultValues: {
      groupName: '',
    },
  });

  useEffect(() => {
    if (groupInfo?.GroupData) {
      reset({
        groupName: groupInfo.GroupData.GroupName,
        groupImg: convertUrlToUploadable(groupInfo.GroupData.GroupImg),
      });
    }
  }, [groupInfo?.GroupData]);

  const submitUpdate = async (data: UpdateGroupInfoFormData) => {
    if (!canManage) return;

    if (groupInfo?.GroupData && data.groupImg && groupInfo.GroupData.GroupImg !== data.groupImg.url) {
      const uploadResult = await upload(data.groupImg);
      data.groupImg.url = uploadResult.FileUrl;
    }

    const result = await updateGroup({
      GroupName: data.groupName,
      GroupImg: data.groupImg?.url,
    });

    if (result.Code === 0) {
      reset(data);
      useConversationSessionStore.getState().updateSession(generateSessionKey('group', groupId), {
        GroupName: data.groupName ?? '',
        GroupImg: data.groupImg?.url ?? undefined,
      });
      queryClient.invalidateQueries(generateGroupInfoQueryOptions(groupId)).catch(console.warn);
    }
  };

  return (
    <PageLayout
      title={canManage ? t('conversation.setting.group.edit-info') : t('conversation.setting.group.info')}
      headerConfig={{
        navigateBack: true,
      }}>
      <form
        className='mx-5 flex flex-1 flex-col justify-between'
        onSubmit={handleSubmit(submitUpdate, console.error)}>
        <FormInput
          disabled={isLoadingGroup || isUpdating || isUploading}
          control={control}
          name='groupName'
          showClearButton
        />

        {canManage && (
          <ProgressButton
            as='submit'
            disabled={!isDirty || isLoadingGroup}
            isLoading={isUpdating || isUploading}
            className='btn btn-primary'
            label='common.confirm'
          />
        )}
      </form>
    </PageLayout>
  );
}

const updateGroupInfoFormScheme = z.object({
  groupName: z.string().trim().min(1, 'errors.string.length|count=1').max(40, 'errors.string.max-length|count=40'),
  groupImg: uploadableScheme.nullish(),
});

type UpdateGroupInfoFormData = z.infer<typeof updateGroupInfoFormScheme>;
